import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Hero from "@/components/feature/Hero";
import CategorySection from "@/components/feature/CategorySection";
import ProductShowcase from "@/components/feature/ProductShowcase";
import { articles } from "@/mocks/articles";
import { allProducts } from "@/mocks/products";

export default function Home() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as "en" | "pt" | "de";
  const safeLang = ["en", "pt", "de"].includes(currentLang) ? currentLang : "en";

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16">
        <Hero />
        <CategorySection />

        {/* Product Showcase */}
        <ProductShowcase categories={allProducts} />

        {/* What is section */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block text-xs font-semibold text-primary-600 uppercase tracking-widest mb-3">{t("hero_eyebrow")}</span>
                <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-6">{t("what_title")}</h2>
                <p className="text-foreground-700 leading-relaxed mb-6">{t("what_text")}</p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 bg-primary-500 text-white font-semibold px-6 py-3 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  {t("what_cta")}
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
              <div className="rounded-xl overflow-hidden border border-background-200/60">
                <img
                  src="https://readdy.ai/api/search-image?query=Clean%20minimal%20home%20office%20setup%20with%20MacBook%20showing%20spreadsheet%20rows%20small%20ceramic%20vase%20with%20dried%20flowers%20soft%20morning%20light%20editorial%20photography%20warm%20cream%20beige%20tones&width=800&height=600&seq=what-is-unique-2026&orientation=landscape"
                  alt="CSSBuy Spreadsheet"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12 md:mb-16">
              <span className="inline-block text-xs font-semibold text-primary-600 uppercase tracking-widest mb-3">Process</span>
              <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4">{t("how_title")}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {[1, 2, 3].map((step) => (
                <div key={step} className="bg-white rounded-xl border border-background-200/60 p-6 md:p-8 text-center">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-lg md:text-xl font-bold text-primary-600">0{step}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-2">{t(`how_step${step}_title`)}</h3>
                  <p className="text-sm text-foreground-600">{t(`how_step${step}_text`)}</p>
                </div>
              ))}
            </div>
            <div className="mt-10 text-center">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 bg-primary-500 text-white font-semibold px-6 py-3 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                {t("how_cta")}
                <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>
        </section>

        {/* 10 Pillar Articles */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12 md:mb-16">
              <span className="inline-block text-xs font-semibold text-primary-600 uppercase tracking-widest mb-3">Resources</span>
              <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4">CSSBuy Spreadsheet Guides</h2>
              <p className="text-foreground-600 max-w-2xl mx-auto">{t("what_text")}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 md:gap-6">
              {articles.map((article) => (
                <a
                  key={article.slug}
                  href={`/article/${article.slug}`}
                  className="group block bg-background-50 rounded-xl border border-background-200/60 overflow-hidden hover:border-primary-300 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-background-100">
                    <img
                      src={article.image}
                      alt={article.title[safeLang] || article.title.en}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded mb-2 ${article.tagColor}`}>
                      {article.tag}
                    </span>
                    <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title[safeLang] || article.title.en}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-2">{article.date} · {article.readTime}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* QC + Shipping + Safety grid */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              <div className="bg-white rounded-xl border border-background-200/60 p-6 md:p-8">
                <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-camera-line text-accent-600 text-lg" />
                </div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-2">{t("qc_title")}</h3>
                <p className="text-sm text-foreground-600 mb-4 leading-relaxed">{t("qc_text")}</p>
                <a href="/article/cssbuy-qc-guide#qc" className="text-sm font-semibold text-accent-600 hover:text-accent-700 transition-colors whitespace-nowrap">
                  {t("qc_cta")} <i className="ri-arrow-right-line" />
                </a>
              </div>

              <div className="bg-white rounded-xl border border-background-200/60 p-6 md:p-8">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-truck-line text-primary-600 text-lg" />
                </div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-2">{t("shipping_title")}</h3>
                <p className="text-sm text-foreground-600 mb-4 leading-relaxed">{t("shipping_text")}</p>
                <a href="/article/cssbuy-shipping-cost#shipping" className="text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap">
                  {t("shipping_cta")} <i className="ri-arrow-right-line" />
                </a>
              </div>

              <div className="bg-white rounded-xl border border-background-200/60 p-6 md:p-8">
                <div className="w-10 h-10 bg-secondary-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-secondary-600 text-lg" />
                </div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-2">{t("safety_title")}</h3>
                <p className="text-sm text-foreground-600 mb-4 leading-relaxed">{t("safety_item1")}, {t("safety_item2")}, {t("safety_item3")}...</p>
                <a href="/article/cssbuy-safety-guide#safety" className="text-sm font-semibold text-secondary-600 hover:text-secondary-700 transition-colors whitespace-nowrap">
                  {t("safety_cta")} <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 md:py-24 bg-foreground-950 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://readdy.ai/api/search-image?query=Soft%20abstract%20textured%20gradient%20background%20warm%20terracotta%20and%20cream%20swirling%20organic%20pattern%20minimal%20artistic%20editorial%20design%20subtle%20texture%20no%20objects&width=1600&height=900&seq=cta-unique-2026&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">{t("cta_title")}</h2>
            <p className="text-white/70 max-w-2xl mx-auto mb-8">{t("cta_text")}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 bg-primary-500 text-white font-semibold px-6 py-3 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                {t("cta_primary")}
                <i className="ri-arrow-right-line" />
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white font-semibold px-6 py-3 rounded-md border border-white/20 hover:bg-white/20 transition-colors whitespace-nowrap"
              >
                {t("cta_secondary")}
                <i className="ri-external-link-line" />
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}